import { Users } from '@platform/client';
import { ProfessionalType } from '@platform/client/schemas';
import { EYLogo, F5Logo, Icon, Logo, ReckittLogo, SAPLogo } from '@platform/components';
import cx from 'classix';
import { ReactElement } from 'react';
import { Link } from 'wouter';
import dataTestids from '../constants/test-ids';
import { LumenDashboardLogo, PhilipsDashboardLogo } from '../dashboard/home/logos/DashboardLogos';
import { ROUTES } from '../routes/consts';
import NavUserMenu from './NavUserMenu';

const CORPORATE_CUSTOMER_LOGO: Record<ProfessionalType, ReactElement | null> = {
  mwi: null, // Not used
  sap: (
    <div style={{ width: '97px' }}>
      <SAPLogo />
    </div>
  ),
  ey: (
    <div style={{ width: '42px' }}>
      <EYLogo />
    </div>
  ),
  f5: (
    <div style={{ width: '48px' }}>
      <F5Logo />
    </div>
  ),
  generic: null,
  kering: null, // Not used
  paypal: null, // Not used
  reckitt: (
    <div style={{ width: '82px' }}>
      <ReckittLogo />
    </div>
  ),
  tsh: null,
  wmp: null, // Not used
  lumen: (
    <div className='-ml-4' style={{ width: '146px' }}>
      <LumenDashboardLogo />
    </div>
  ),
  philips: (
    <div className='-ml-4' style={{ width: '146px' }}>
      <PhilipsDashboardLogo />
    </div>
  ),
};

const AppHeader = ({
  onMobileMenuToggle,
  isMobileMenuOpen,
}: {
  onMobileMenuToggle: () => void;
  isMobileMenuOpen: boolean;
}) => {
  const { data: user } = Users.useGetCurrentUser();

  const secondaryLogo =
    user?.accountType === 'experteer' ? CORPORATE_CUSTOMER_LOGO[user.professionalType]
    : user?.accountType === 'organization' && user.organization?.avatar ?
      // Org logos are uploaded at whatever size ratio the org used unlike the
      // fixed-dimension partner SVGs above. `object-contain` inside a capped box scales the
      // image down to fit
      <img
        src={user.organization.avatar}
        alt={`${user.organization.name} logo`}
        className='max-h-10 max-w-40 w-auto h-auto object-contain'
      />
    : null;

  return (
    <div
      className={cx(
        'sticky top-0 z-40 bg-white px-4 lg:px-8 flex w-full gap-4 min-w-0 h-16 items-center',
        'border-b border-gray-100 whitespace-nowrap',
      )}
      data-testid={dataTestids.DASHBOARD.HEADER}
    >
      <Link
        to={ROUTES.DASHBOARD}
        className='flex h-full items-center w-[224px]'
        data-testid={dataTestids.LAYOUT.HEADER.LOGO}
      >
        <Logo type='mw-text' variant='blue' />
      </Link>
      {secondaryLogo && (
        <>
          <div className='w-px h-8 bg-gray-100' />
          <div
            className='flex h-full items-center'
            data-testid={dataTestids.LAYOUT.HEADER.PARTNER_LOGO}
          >
            {secondaryLogo}
          </div>
        </>
      )}
      <div className='ml-auto flex items-center gap-4'>
        <div className='hidden md:block'>
          <NavUserMenu />
        </div>
        <button
          className='md:hidden ml-2 cursor-pointer p-2'
          onClick={onMobileMenuToggle}
          data-testid={dataTestids.LAYOUT.HEADER.MOBILE_SIDEPANEL_BUTTON}
        >
          <Icon type={isMobileMenuOpen ? 'x-close' : 'menu-02'} size='24' stroke='gray-500' />
        </button>
      </div>
    </div>
  );
};

export default AppHeader;
